/* eslint-disable no-param-reassign */
import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import sanitizeHtml from "sanitize-html";
import {
    BLOG_BUTTON_TEXT,
    BLOG_BUTTON_TYPE,
    BLOG_RELATED_POSTS_HEADING,
} from "@qlik/qlik/src/lib/constants/blog-constants";
import RichText from "@qlik/qlik/src/components/RichText";
import { CURRENCY_VALUES } from "@qlik/qlik/src/lib/constants/pricing-constants";
import { slugTermMapping } from "@qlik/qlik/src/lib/constants/news-constants";
import {
    TYPE_BLOG,
    TYPE_CTA,
    TYPE_MARKETO_FORM,
    TYPE_MICRO_ASSET,
    TYPE_RESOURCE,
    TYPE_NEWS,
} from "@qlik/qlik/src/lib/constants/contentful-types";
import {
    RESOURCE_BUTTON_TEXT,
    RESOURCE_BUTTON_TYPE,
    RESOURCE_GATED_CONTENT_RENDERING_NAME,
    RESOURCE_PATHNAME,
} from "@qlik/qlik/src/lib/constants/resource-constants";
import { BLOG_BUTTON_KEY, RESOURCE_BUTTON_KEY } from "@qlik/qlik/src/lib/constants/microcopy-constants";
import { BLOG_PATHNAME, NEWS_PATHNAME, RESOURCE_LIBRARY_PATHNAME } from "@qlik/qlik/gatsbyNode/constants";
import { AS_ANCHOR } from "../constants/cta-constants";

/**
 * getSafeMediaAssetUrl returns the secure url (i.e. the https url from cloudinary) for the image in the
 * @index position within the contentful media entry represented by @assets
 */
const getSafeMediaAssetUrl = (assets, index = 0) => {
    if (assets?.length > index) {
        return assets[index].secure_url;
    }
    return null;
};

/**
 * getSafeCta returns the CTA (Call to Action) at a given @index
 * within the array of contentful CTAs represented by @ctas
 */
const getSafeCta = (ctas, index = 0) => {
    if (ctas?.length > index) {
        return ctas[index];
    }
    return null;
};

/**
 * debounce returns a function that delays the invocation of the provided function
 * until after a specified number of milliseconds have elapsed since the last time it was invoked
 */
const debounce = (func, milliseconds = 200) => {
    let timer;

    return (event) => {
        if (timer) {
            clearTimeout(timer);
        }

        timer = setTimeout(func, milliseconds, event);
    };
};

/**
 * filterValidCta filters out invalid CTAs from @cta array, that do not have an internal or external link,
 * a Popup link behavior, or query parameters within the array of contentful CTAs represented by @cta
 */
const filterValidCta = (cta) => {
    cta = Array.from(new Set(cta?.map(JSON.stringify))).map(JSON.parse);
    return Array.isArray(cta)
        ? cta.filter(
              (link) =>
                  !!link.internalLink ||
                  !!link.externalUrl ||
                  link.linkBehavior === "Popup" ||
                  link.queryParams?.queryParams
          )
        : [];
};

/**
 * renderNode is a recursive function that takes a node object as an argument and returns a corresponding React element based on the node's type
 * A @node refers to an object that represents a part of a document in a tree structure, such as a piece of text, a paragraph, a list item, an unordered list, or a document itself
 * This function is typically used to render rich text content that has been stored in a structured format, such as Contentful's rich text fields
 */
const renderNode = (node) => {
    if (node) {
        switch (node.nodeType) {
            case "text":
                return <span>{node.value}</span>;

            case "paragraph":
                return <p>{node.content.map(renderNode)}</p>;

            case "list-item":
                return <li>{node.content.map(renderNode)}</li>;

            case "unordered-list":
                return <ul>{node.content.map(renderNode)}</ul>;

            case "document":
                return <div>{node.content.map(renderNode)}</div>;

            default:
                return null;
        }
    }
    return null;
};

/**
 * getArticleReadingStats calculates and returns the reading stats for an article based on its @richTextContent
 * The function counts the number of words in the content and estimates the reading time
 * based on a given @wordsPerMinute rate
 */
const getArticleReadingStats = (richTextContent, wordsPerMinute = 150) => {
    try {
        if (!richTextContent || typeof richTextContent !== "string") {
            throw new Error("Rich text content is not supported");
        }

        const parsedRichContent = JSON.parse(richTextContent);
        const plainText = documentToPlainTextString(parsedRichContent);
        const wordCount = plainText.trim().split(/\s+/).length;
        const minutes = Math.ceil(wordCount / wordsPerMinute);

        return {
            wordCount,
            readTime: `${minutes} min read`,
        };
    } catch (error) {
        return {
            wordCount: 0,
            readTime: "1 min read",
        };
    }
};

/**
 * convertStringToScriptSrc extracts the 'src' attribute value from a script tag within a string represented by @content
 * If a match is found, it returns the 'src' value
 */
const convertStringToScriptSrc = (content) => {
    const scriptTagRegex = /<script.*?src=['"](.*?)['"].*?>/g;
    const match = scriptTagRegex.exec(content);

    if (match) {
        return match[1];
    }
    return null;
};

/**
 * getScriptSrc parses the @content(rich text content) to plain text,
 * extracts the 'src' attribute value from any script tags within the @content
 * If a match is found, it returns the 'src' value
 */
const getScriptSrc = (content) => {
    const parsedRichContent = JSON.parse(content);
    const plainText = documentToPlainTextString(parsedRichContent);
    const srcValue = convertStringToScriptSrc(plainText);

    return srcValue;
};

/**
 * getPagePath returns the page path based on the window object( @windowObj )
 * which finds the key in the @slugTermMapping that matches the pathname in the window object
 * slugTermMapping {
 *  "/company/in-the-news/": "In The News",
 *  "/company/press-room/": "Press Release",
 * }
 */
const getPagePath = (windowObj) => {
    if (typeof windowObj === "undefined") return "";
    const foundKey = Object.keys(slugTermMapping).find((path) => {
        const formattedPath = path.endsWith("/") ? path.slice(0, -1) : path;
        return windowObj.location?.pathname.includes(formattedPath);
    });
    return slugTermMapping[foundKey];
};

/**
 * scrollToElement scrolls to the given element smoothly
 * The element is specified by the parameter @elementRef , which is a reference to the element
 */
const scrollToElement = (elementRef) => elementRef?.current?.scrollIntoView({ behavior: "smooth" });

/**
 * getCardMedia returns the media of a Contentful card
 * It checks for various properties in the @cardData object
 * and returns the first one that exists
 * If no media is found, it returns an empty string
 */
const getCardMedia = (cardData) =>
    cardData?.icon?.[0] ||
    cardData?.mediaIcon ||
    cardData?.resourceIcon ||
    cardData?.media?.[0] ||
    cardData?.featuredImage ||
    cardData?.image ||
    "";

/**
 * getCardDescriptionText returns the description text of a Contentful card based on the @cardData
 * It checks for 'excerpt' and 'description' properties in the card data and
 * returns the first one that exists
 */
const getCardDescriptionText = (cardData) => {
    let cardDescription = "";
    if (cardData?.excerpt) {
        cardDescription = cardData.excerpt.excerpt;
    } else if (cardData?.description) {
        cardDescription = cardData.description.description;
    }
    return cardDescription;
};

/**
 * getCardEyebrowText returns the eyebrow text of a Contentful card based on the @cardData
 * It checks for 'eyebrowText' and 'resourceType' properties in the card data and
 * returns the first one that exists
 */
const getCardEyebrowText = (cardData) => {
    let cardEyebrowText = "";
    if (cardData?.eyebrowText) {
        cardEyebrowText = cardData.eyebrowText;
    } else if (cardData?.resourceType?.[0]) {
        cardEyebrowText = cardData.resourceType[0].title;
    }
    return cardEyebrowText;
};

/**
 * constructVidyardPlayerUrl constructs a Vidyard player URL
 * by replacing certain parts of the given @url
 * This function is useful when you want to embed a Vidyard video in a webpage
 * and you want the video to start playing immediately when the page loads
 */
const constructVidyardPlayerUrl = (url) => url.replace("share", "play").replace("watch/", "");

/**
 * getVidyardURL checks if the provided @url is a Vidyard URL
 * If it is, constructs a Vidyard player URL and returns it
 * If not, returns an empty string indicating that the provided URL is not a Vidyard URL
 */
const getVidyardURL = (url) => {
    if (!url) return "";

    const vidyardPattern = /(?:https?:\/\/)?(?:www\.)?vidyard\.com\/([a-zA-Z0-9]+)/;
    if (!vidyardPattern.test(url)) return "";

    return constructVidyardPlayerUrl(url);
};

const getVidyardUUID = (url) => {
    if (!url) return "";

    const vidyardPattern = /(?:https?:\/\/)?(?:www\.)?vidyard\.com\/([a-zA-Z0-9]+)/;
    if (!vidyardPattern.test(url)) return "";

    return url.match(vidyardPattern)[1];
};

/**
 * getVidyardCoverImageUrl constructs a Vidyard cover image URL from a provided Vidyard @url
 * If the provided URL is not a Vidyard URL, returns an empty string
 */
const getVidyardCoverImageUrl = (url) => {
    if (!getVidyardURL(url)) return "";
    return constructVidyardPlayerUrl(url).concat(".jpg");
};

/**
 * getAmountBySelectedCurrency returns the converted @amount for the selected currency based on USD.
 * The @currencyId of the selected currency. This corresponds to the 'id' property of the objects in the CURRENCY_VALUES array
 * CURRENCY_VALUES [
 *  { id: 0, symbol: "$", label: "USD", ratio: 1 },
 *  { id: 1, symbol: "€", label: "EURO", ratio: 0.93 },
 *  { id: 2, symbol: "£", label: "GBP", ratio: 0.8 },
 * ]
 */
const getAmountBySelectedCurrency = (amount, currencyId) => {
    const currencyObject = CURRENCY_VALUES.find((currency) => currency.id === currencyId);
    const { ratio } = currencyObject;
    const convertedAmount = amount * ratio;
    return convertedAmount;
};

/**
 * getMicrocopyValueFromKeys returns an array of microcopy values corresponding to the provided @keys
 * The @group object containing contentful microcopy data
 * The @fuzzy flag indicates whether to use fuzzy matching when searching for keys (usecase: having expand/collapse buttons on multiple pricing pages)
 */
const getMicrocopyValueFromKeys = (group = {}, keys = [], fuzzy = false) => {
    return keys.map(
        (key) => group?.microcopy?.find((copy) => (fuzzy ? copy.key.includes(key) : copy.key === key))?.value?.value
    );
};

/**
 * generateRelatedPostsData generates related posts data based on the provided @items and @title
 * The array of @items contains the blog posts
 * @sectionTitle consider as the title of the whole section
 */
const generateRelatedPostsData = (items, title, sectionTitle) => {
    let relatedPostsData = null;
    const filteredItems = items.filter((item) => item.title !== title).slice(0, 3);

    if (filteredItems) {
        relatedPostsData = {
            title: sectionTitle,
            numberOfColumns: 3,
            cardItems: filteredItems,
            backgroundColor: "Gradient",
            additionalConfiguration: {
                spacingTop: {
                    paddingMobile: "S",
                    paddingDesktop: "4XL",
                },
                spacingBottom: {
                    paddingMobile: "S",
                    paddingDesktop: "4XL",
                },
            },
        };
    }
    return relatedPostsData;
};

/**
 * getThemeSettings executes a GraphQL query to fetch theme settings from Contentful
 * and returns the first node from the 'allContentfulThemeSettings' query result,
 * which contains theme settings data
 */
const getThemeSettings = () => {
    const query = useStaticQuery(graphql`
        query themeSettingsQuery {
            allContentfulThemeSettings {
                nodes {
                    id
                    name
                    socialSharingPlatforms
                    siteMetaData {
                        key
                        value
                        values {
                            values
                        }
                    }
                    blogMicrocopyGroup {
                        ...MicrocopyGroup
                    }
                    resourceMicrocopyGroup {
                        ...MicrocopyGroup
                    }
                    eventMicrocopyGroup {
                        ...MicrocopyGroup
                    }
                    newsMicrocopyGroup {
                        ...MicrocopyGroup
                    }
                    dataSourceMicrocopyGroup {
                        ...MicrocopyGroup
                    }
                }
            }
        }
    `);

    return query.allContentfulThemeSettings?.nodes?.[0];
};

/**
 * getInternalLinkCta generates a Call to Action (CTA) object for internal links
 * It uses the provided @prefix , @slug , and @contentType to construct the CTA object
 * The function also uses theme settings and @microcopyGroup to fetch button text based on the content type
 * If a CTA object is provided, it will be merged into the generated CTA object
 */
const getInternalLinkCta = (prefix, slug, contentType, microcopyGroup, cta = {}) => {
    const themeSettings = getThemeSettings();
    const blogMicrocopies = themeSettings?.blogMicrocopyGroup || {};
    const resourceMicrocopies = themeSettings?.resourceMicrocopyGroup || {};

    const [blogButtonText] = getMicrocopyValueFromKeys(blogMicrocopies, [BLOG_BUTTON_TEXT]);
    const [resourceButtonText] = getMicrocopyValueFromKeys(resourceMicrocopies, [RESOURCE_BUTTON_TEXT]);

    // mapping for types
    const internalLinkCtaTypeMapping = {
        [TYPE_BLOG]: {
            name: blogButtonText,
            text: blogButtonText,
            type: BLOG_BUTTON_TYPE,
            microcopyButtonKey: BLOG_BUTTON_KEY,
        },
        [TYPE_RESOURCE]: {
            name: resourceButtonText,
            text: resourceButtonText,
            type: RESOURCE_BUTTON_TYPE,
            microcopyButtonKey: RESOURCE_BUTTON_KEY,
        },
    };

    let { name, text, type, microcopyButtonKey } = internalLinkCtaTypeMapping[contentType] ?? {};
    if (microcopyGroup) {
        const buttonText = getMicrocopyValueFromKeys(microcopyGroup, [microcopyButtonKey])?.[0] || null;
        name = text = buttonText;
    }

    if (!name) return []; // render nothing

    return [
        {
            __typename: TYPE_CTA,
            id: `${prefix}-${slug}`,
            name,
            text,
            type,
            ...cta, // override above props from cta if available
            internalLink: {
                slug: `${prefix}/${slug}`,
            },
        },
    ];
};

const getLinkFromCta = (cta) => {
    const ctaEl = Array.isArray(cta) ? cta?.[0] : cta;

    // construct the link based on the data from the CTA
    const cardLink =
        (ctaEl?.internalLink && internalUrl(ctaEl.internalLink, ctaEl.queryParams)) ||
        appendQueryParamsToExternalUrl(ctaEl.externalUrl, ctaEl);

    return cardLink;
};

/**
 * getGatedContentComponent checks if the provided @resource is gated,
 * which means it includes at least one Gated Content rendering
 * A Gated Content rendering requires at least a content card and a form
 */
const getGatedContentComponent = (resource) =>
    resource?.components?.find(
        (component) =>
            component.frontEndComponent === RESOURCE_GATED_CONTENT_RENDERING_NAME &&
            component.cardItems?.length >= 2 &&
            component.cardItems?.some((card) => card.__typename === TYPE_MARKETO_FORM)
    );

/**
 * getResourceInternalLinkCta generates a Call to Action (CTA) object for internal links in resource pages
 * It checks if the provided @resource is an inner page, a micro asset, or has an external URL
 * If it is, it generates a CTA object using the resource's slug and microcopy group
 * If a CTA object is provided, it will be merged into the generated CTA object
 */
const getResourceInternalLinkCta = (resource, ctaList = []) => {
    const cta = ctaList?.[0];
    const isResourceInnerPage =
        ctaList.length === 0 || cta?.internalLink?.__typename === TYPE_MICRO_ASSET || cta?.externalUrl;
    if (isResourceInnerPage) {
        return getInternalLinkCta(RESOURCE_PATHNAME, resource?.slug, TYPE_RESOURCE, resource?.microcopyGroup, {
            ...cta,
            as: AS_ANCHOR,
        });
    }

    return ctaList;
};

/**
 * getValidatedCategoryFilters validates the category filters based on the @currentCategory and @resourceCategoryData
 * It creates a list of category filters to be rendered, checks if items exist in other categories, and disables those that don't
 */
const getValidatedCategoryFilters = (currentCategory, resourceCategoryData) => {
    // create category filters list to be rendered
    const validatedCategoryFilters = JSON.parse(JSON.stringify(resourceCategoryData));

    // isolate data of current category to check against other categories
    const currentCategoryData = resourceCategoryData.find((categoryData) => categoryData.id === currentCategory.id);

    // get a list of item IDs for checking inside each other group for existence
    const currentCategoryItemIds = currentCategoryData?.subTerms
        ? currentCategoryData.subTerms
              .filter((subTerm) => currentCategory.value.includes(subTerm.title))
              .flatMap((item) => item.items)
        : [];

    // ignore current category from updating
    const filteredCategoryData = validatedCategoryFilters.filter((category) => category.id !== currentCategory.id);

    filteredCategoryData.forEach(({ subTerms }) => {
        // for each Category Group in other categories check if item exists at least once
        subTerms?.forEach((subTerm) => {
            // if no filter is selected enable all
            const isEnabled =
                currentCategoryItemIds.length === 0
                    ? true
                    : subTerm.items.some((termId) => currentCategoryItemIds.includes(termId));
            // eslint-disable-next-line no-param-reassign
            subTerm.isDisabled = !isEnabled;
        });
    });

    return validatedCategoryFilters;
};

/**
 * menuResourceCardLink generates a language-prefixed link for the provided @data 's Call to Action (CTA)
 * If the CTA has an internal link, it returns a language-prefixed path with the internal link's slug
 * If the CTA has an external URL, it returns the external URL
 * If no CTA is provided, it returns a language-prefixed path with the resource pathname and the data's slug
 */
const menuResourceCardLink = (data) => {
    const cta = data?.cta?.[0];

    if (cta && cta?.internalLink) {
        return `/${cta.internalLink?.slug}`;
    }

    return cta ? cta.externalUrl : `/${RESOURCE_PATHNAME}/${data.slug}`;
};

/**
 * getValueByKey returns the value associated with a given @key in an @array of objects
 */
const getValueByKey = (array, key) => {
    if (Array.isArray(array)) {
        const resultItem = array.find((item) => item.key === key);
        return resultItem ? resultItem?.values?.values : null;
    }

    return null;
};

/**
 * getSiteURL retrieves the site URL from the theme settings or from the environment variable if not found in the theme settings
 */
const getSiteURL = () => {
    const defaultSiteURL = process.env.GATSBY_SITE_URL;
    const themeSettings = getThemeSettings();
    const siteMetaDataArray = themeSettings?.siteMetaData || [];
    const siteUrl = getValueByKey(siteMetaDataArray, "siteUrl") || defaultSiteURL;

    return siteUrl;
};

/**
 * appendQueryParamsToExternalUrl appends query parameters to an external URL
 * If the @url is not valid, it returns the query parameters as a string or an empty string if no query parameters are provided
 * (also apparently used for internal URLs 🙄)
 * @data object containing the query parameters
 */
const appendQueryParamsToExternalUrl = function(url, data) {
    if (!url) return data?.queryParams?.queryParams || "";
    try {
        // Create a new URL object
        let urlObj = new URL(url, getSiteURL());

        // Get the search parameters from the URL
        let params = new URLSearchParams(urlObj.search);

        // Create a new URLSearchParams object from the queryParams
        let newParams = new URLSearchParams(data?.queryParams?.queryParams || undefined);

        // Append each new parameter to the existing parameters
        for (let [key, value] of newParams.entries()) {
            params.append(key, value);
        }

        // Update the search parameters of the URL
        urlObj.search = params.toString();

        // Return the updated URL as a string, don't prepend site URL to internal links (except /sso/saml I guess...).
        return urlObj?.pathname?.startsWith("/sso/saml")
            ? urlObj.toString()
            : urlObj.toString().replace(getSiteURL(), "");
    } catch (e) {
        // Return url if creating the query params failed
        return url;
    }
};

/**
 * internalUrl generates a URL with @queryParams for the provided @internalLink
 * It checks the type of the internal link and constructs the URL accordingly
 * If the internal link is a micro asset, it uses the asset's original secure URL (cloudinary)
 * If query parameters are provided, they are appended to the URL
 */
const internalUrl = function(internalLink, queryParams) {
    let url = internalLink?.slug?.startsWith("/") ? internalLink?.slug : `/${internalLink?.slug}`;
    if (internalLink?.__typename === TYPE_RESOURCE) {
        // eslint-disable-line no-underscore-dangle
        url = `/${RESOURCE_LIBRARY_PATHNAME}${url}`;
    } else if (internalLink?.__typename === TYPE_NEWS) {
        // eslint-disable-line no-underscore-dangle
        url = `/${NEWS_PATHNAME}${url}`;
    } else if (internalLink?.__typename === TYPE_BLOG) {
        // eslint-disable-line no-underscore-dangle
        url = `/${BLOG_PATHNAME}${url}`;
    } else if (internalLink?.__typename === TYPE_MICRO_ASSET) {
        // eslint-disable-nextline no-underscore-dangle
        url = internalLink?.asset[0]?.original_secure_url;
    }

    if (typeof queryParams?.queryParams === "string") {
        // Append the query string to the URL
        url += "?" + queryParams.queryParams;
    }
    return url;
};

/**
 * addParamToURL adds a parameter to the given @url
 * If the parameter already exists in the URL, it does not add it again
 * If the URL is invalid, it returns the original URL
 * @paramName The name of the parameter to be added
 * @paramValue The value of the parameter to be added
 */
const addParamToURL = (url, paramName, paramValue) => {
    try {
        const urlObject = new URL(url);
        // Check if the parameter already exists in the URL
        if (!urlObject.searchParams.has(paramName)) {
            // If not, add the parameter
            urlObject.searchParams.set(paramName, paramValue);
        }
        return urlObject.toString();
    } catch (error) {
        // Handle invalid URL or other errors
        return url;
    }
};

/**
 * renderSanitizedHtmlContent sanitizes HTML @rawContent by applying a set of allowed tags and attributes
 * This helps to mitigate security risks from potentially harmful input
 */
const renderSanitizedHtmlContent = (rawContent) => {
    const defaultAllowedTags = sanitizeHtml.defaults.allowedTags;
    // Extend the allowed tags with more tags
    const extendedAllowedTags = [...defaultAllowedTags, "img", "picture", "source", "iframe"];

    const sanitizedHtmlContent = sanitizeHtml(rawContent, {
        allowedAttributes: {
            "*": ["style", "class", "id"],
            a: ["href", "name", "target"],
            img: ["src", "srcset", "alt", "title", "width", "height", "loading"],
            source: ["media"],
            iframe: ["title", "src", "id", "width", "height", "style", "scrolling", "frameborder"],
        },
        allowedTags: extendedAllowedTags,
    });

    return sanitizedHtmlContent;
};

/**
 * getCardBodyContent returns the card description text based on the model that stored contentful @cardData
 * @className an optional class name to be applied to the paragraph element
 */
const getCardBodyContent = (cardData, className = null) => {
    let cardBodyContent = "";
    if (cardData?.description?.description) {
        cardBodyContent = (
            <p
                className={className}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(cardData.description.description) }}
            />
        );
    } else if (cardData?.body?.raw) {
        cardBodyContent = <RichText className={className} data={cardData} />;
    }
    return cardBodyContent;
};

/**
 * createParameterizedUrl returns the parameterized URL as a string or an object by checking the given internal or external links
 * The @data object containing the page link or external URL
 */
const createParameterizedUrl = (data) => {
    let pageUrl = "#";
    if (data?.pageLink || data?.externalUrl) {
        pageUrl = data?.pageLink
            ? internalUrl(data?.pageLink, data?.queryParams)
            : appendQueryParamsToExternalUrl(data?.externalUrl, data);
    }

    return pageUrl;
};

/**
 * handleKeyboardEventBehavior triggers a callback function when a specified keyboard event occurs
 */
const handleKeyboardEventBehavior = (event, callback, keyCodes = ["Enter", "Space"]) => {
    if (keyCodes.length && keyCodes.includes(event.code)) {
        callback();
    }
};

/**
 * isFullyQualifiedUrl checks if the provided @value is a fully qualified URL
 */
const isFullyQualifiedUrl = (value) => {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(value);
};

export {
    getSafeMediaAssetUrl,
    debounce,
    getSafeCta,
    filterValidCta,
    renderNode,
    getArticleReadingStats,
    getScriptSrc,
    scrollToElement,
    getPagePath,
    getCardMedia,
    getCardDescriptionText,
    getCardEyebrowText,
    getCardBodyContent,
    getVidyardURL,
    getVidyardUUID,
    getVidyardCoverImageUrl,
    getAmountBySelectedCurrency,
    getMicrocopyValueFromKeys,
    generateRelatedPostsData,
    getInternalLinkCta,
    getLinkFromCta,
    getGatedContentComponent,
    getValidatedCategoryFilters,
    isFullyQualifiedUrl,
    menuResourceCardLink,
    getResourceInternalLinkCta,
    getThemeSettings,
    getSiteURL,
    getValueByKey,
    appendQueryParamsToExternalUrl,
    internalUrl,
    addParamToURL,
    renderSanitizedHtmlContent,
    createParameterizedUrl,
    handleKeyboardEventBehavior,
};
